/**
 * ObjectHeader (TMMPDM-0034, OBJ-001 / OBJ-004 / BREAD-001 / BREAD-003) — the top of a tenant or
 * reseller detail page: breadcrumbs whose destinations are real links ("Tenants / ACME Corp"), the
 * object name as the `h1`, its status chips, the identifier in monospace with a Copy button (quiet
 * "Copied" toast), the secondary id beside it, and the page's actions as in `PageHeader`. Sticky
 * under the app bar like `PageHeader`.
 */
import { useRef, type ReactNode, type Ref } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useCopy } from '@/hooks/useCopy';
import { useNotify } from './NotifyProvider';
import {
  HeaderActions,
  HeaderCrumbs,
  StickyHeader,
  type Crumb,
  type HeaderAction,
} from './headerChrome';
import { titleParts, usePageTitle } from './usePageTitle';

export interface ObjectHeaderProps {
  breadcrumbs: Crumb[];
  name: string;
  /** Status chips (status, standing, Can't sell…). */
  status?: ReactNode;
  /**
   * The user-facing identifier (the slug) — copyable. Absent while the page loads and when the
   * object is not found: those states render this same header, so the page keeps one `h1` element.
   */
  identifier?: string;
  /** The backing id (saas-auth `tenants.id`, the reseller id), shown beside it. */
  secondaryIdentifier?: string;
  primaryAction?: HeaderAction;
  secondaryActions?: HeaderAction[];
  overflowActions?: HeaderAction[];
  /** Replaces the action row when the page owns the controls (a menu, not only buttons). */
  actions?: ReactNode;
  /** Sits beside the object name (the header ⋮). */
  nameAdornment?: ReactNode;
  /** A line under the identifier (created date). */
  meta?: ReactNode;
  /** The `h1` is programmatically focusable (`tabIndex -1`) so a screen can move focus to it. */
  headingRef?: Ref<HTMLHeadingElement>;
}

export function ObjectHeader({
  breadcrumbs,
  name,
  status,
  identifier,
  secondaryIdentifier,
  primaryAction,
  secondaryActions,
  overflowActions,
  actions,
  nameAdornment,
  meta,
  headingRef,
}: ObjectHeaderProps) {
  const { t } = useCopy();
  const notify = useNotify();
  usePageTitle(...titleParts(name, breadcrumbs));
  const copyLabel = t('components.copy', 'Copy {{identifier}}', { identifier: identifier ?? '' });

  const identifierRef = useRef<HTMLElement>(null);

  // No clipboard (an insecure origin) or a refused write (TMMPDM-0035, FEED-003): say what to do
  // instead and select the identifier, ready to copy by hand.
  const copy = async () => {
    if (identifier === undefined) return;
    try {
      await navigator.clipboard.writeText(identifier);
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
      const node = identifierRef.current;
      if (node) window.getSelection()?.selectAllChildren(node);
    }
  };

  return (
    <StickyHeader>
      <HeaderCrumbs crumbs={breadcrumbs} />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ alignItems: { md: 'flex-end' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h1" component="h1" ref={headingRef} tabIndex={-1}>
              {name}
            </Typography>
            {nameAdornment}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {identifier !== undefined ? (
              <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                <Typography
                  ref={identifierRef}
                  variant="body2"
                  color="text.secondary"
                  sx={(theme) => theme.typography.mono}
                >
                  {identifier}
                </Typography>
                <Tooltip title={copyLabel}>
                  <IconButton size="small" aria-label={copyLabel} onClick={() => void copy()}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null}
            {secondaryIdentifier ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={(theme) => theme.typography.mono}
              >
                {t('components.idLabel', 'ID {{id}}', { id: secondaryIdentifier })}
              </Typography>
            ) : null}
            {status}
          </Stack>
          {meta}
        </Box>
        {actions ?? (
          <HeaderActions
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
            overflowActions={overflowActions}
          />
        )}
      </Stack>
    </StickyHeader>
  );
}
